import StepFind from './assets/ronda/step-find.webp';
import StepStart from './assets/ronda/step-start.webp';
import StepPrice from './assets/ronda/step-price.webp';
import StepReturn from './assets/ronda/step-return.webp';
import PosterCity from './assets/ronda/poster-city.webp';
import PosterMonument from './assets/ronda/poster-monument.webp';
import PosterCathedral from './assets/ronda/poster-cathedral.webp';

export const languages = ['es', 'en'];

export const pricing = {
  unlock: 5,
  perMinute: 0.25,
  exampleMinutes: 30,
};

// Whole amounts show without decimals; Spanish uses a decimal comma.
export const formatBs = (amount, lang) => {
  const text = Number.isInteger(amount) ? `${amount}` : amount.toFixed(2);
  return lang === 'es' ? text.replace('.', ',') : text;
};

const hrefs = ['#how-it-works', '#city', '#pricing', '#help'];
const stepImages = [StepFind, StepStart, StepPrice, StepReturn];
const posterImages = [PosterCity, PosterMonument, PosterCathedral];
const vehicleColors = ['bg-scooter', 'bg-ronda'];

const en = {
  meta: {
    title: 'Ronda · Your next great plan starts here',
    description:
      'Ronda: scooters and e-bikes for the trips nearby in Santa Cruz de la Sierra. Your next great plan starts here.',
  },
  skipLink: 'Skip to content',
  logoLabel: 'Ronda, back to top',
  languageLabel: 'Language',
  primaryNavLabel: 'Primary',
  footerNavLabel: 'Footer',
  navigation: ['How it works', 'The city', 'Pricing', 'Help'],
  hero: {
    place: 'Santa Cruz de la Sierra · Equipetrol',
    title: ['Your next great plan', 'starts here.'],
    subtitle: 'Scooters and e-bikes for the trips nearby.',
  },
  stepsSection: {
    eyebrow: 'How it works',
    title: "Four steps. That's it.",
    text: 'Everything happens in the app: you see the vehicle, the price, and where to return it before you ride.',
  },
  steps: [
    { title: 'Find', text: 'Pick a vehicle on the map or scan its code.' },
    { title: 'Start', text: 'Check the price and wait for confirmation before you go.' },
    { title: 'Ride', text: 'You pay to unlock, then for each minute started. Nothing else.' },
    { title: 'Return', text: 'Come back to a marked spot and confirm the ride has ended.' },
  ],
  city: {
    eyebrow: 'The city',
    title: 'For what is close by.',
    text: 'Santa Cruz is shade, flowering trees, and wide streets. Ronda is for the short trips: the coffee, the square, the office, the ride home.',
    imageAlt: 'Poster of Santa Cruz: downtown towers, red roofs, and clouds in a blue sky.',
    vehicles: [
      { name: 'Scooter', text: 'Light and direct for the blocks you ride every day.' },
      { name: 'E-bike', text: 'A seat, a little help, and the afternoon ahead.' },
    ],
  },
  postersSection: {
    eyebrow: 'Santa Cruz',
    title: 'What you notice when you slow down.',
    featureTitle: 'Moving is also looking.',
    featureText: 'The late light, the brick, and the Santa Cruz sky are on every Ronda screen.',
  },
  posters: [
    {
      title: 'Your city. Another look.',
      text: 'The extraordinary is close, too.',
      alt: 'Illustration of Santa Cruz at dusk, with the river and the city lights.',
    },
    {
      title: 'Give your day some air.',
      text: 'Find a scooter or an e-bike a few steps away.',
      alt: 'Illustration of the Cristo Redentor of Santa Cruz under a blue sky.',
    },
    {
      title: 'Go write your story.',
      text: 'Unlock in seconds and ride the city at your pace.',
      alt: 'Illustration of the Santa Cruz cathedral tower among palm trees.',
    },
  ],
  pricing: {
    eyebrow: 'Pricing',
    title: 'Clear from the first minute.',
    text: 'You see the price before you start and the receipt when you finish.',
    unlock: 'To unlock',
    perMinute: 'Per minute started',
    example: (minutes) => `For example, a ${minutes}-minute ride costs`,
    imageAlt:
      'iPhone showing the Ronda map in Equipetrol, with scooters, an e-bike, and a button to scan a vehicle code.',
  },
  faqSection: {
    eyebrow: 'Help',
    title: 'Before your first ride.',
    text: 'The questions people ask, answered the same way as in the app.',
  },
  faq: [
    {
      question: 'Where can I return the vehicle?',
      answer:
        'At a return spot marked on the map, inside the operating area. Request the end and wait for confirmation.',
    },
    {
      question: 'How is the price calculated?',
      answer: `Bs ${formatBs(pricing.unlock, 'en')} to unlock and Bs ${formatBs(
        pricing.perMinute,
        'en'
      )} for each minute started. You see the price before you start and the receipt when you finish.`,
    },
    {
      question: 'Does closing the app end the ride?',
      answer:
        'No. Until “Ride ended” appears, the return is not confirmed. Switching tabs, closing the app, or signing out does not end a ride.',
    },
    {
      question: 'Why do you ask me to verify my identity?',
      answer:
        'Before your first ride, the app checks your identity with your document and a selfie. It happens once and stays on your account.',
    },
    {
      question: "I can't start the ride. What do I do?",
      answer:
        'Pick an available vehicle and check its price. If the unlock is still confirming, wait for the result before you use it.',
    },
  ],
  closing: {
    title: 'Your next ride starts nearby.',
    text: 'Create your account, verify your identity once, and pick a vehicle on the map.',
    badge: 'Coming soon on iPhone',
    link: 'See how it works',
  },
  footer: {
    tagline: 'Scooters and e-bikes for the trips nearby.',
    place: 'Santa Cruz de la Sierra, Bolivia',
    note: 'Preview: vehicles and payments are simulated.',
  },
};

const es = {
  meta: {
    title: 'Ronda · Tu próximo qué buen plan empieza acá',
    description:
      'Ronda: scooters y bicis eléctricas para moverte cerca en Santa Cruz de la Sierra. Tu próximo qué buen plan empieza acá.',
  },
  skipLink: 'Saltar al contenido',
  logoLabel: 'Ronda, volver arriba',
  languageLabel: 'Idioma',
  primaryNavLabel: 'Principal',
  footerNavLabel: 'Pie de página',
  navigation: ['Cómo funciona', 'La ciudad', 'Precios', 'Ayuda'],
  hero: {
    place: 'Santa Cruz de la Sierra · Equipetrol',
    title: ['Tu próximo qué buen plan', 'empieza acá.'],
    subtitle: 'Scooters y bicis eléctricas para moverte cerca.',
  },
  stepsSection: {
    eyebrow: 'Cómo funciona',
    title: 'Cuatro pasos. Eso es todo.',
    text: 'Todo pasa en la app: ves el vehículo, el precio y dónde devolverlo antes de salir.',
  },
  steps: [
    { title: 'Encuentra', text: 'Elige un vehículo en el mapa o escanea su código.' },
    { title: 'Empieza', text: 'Revisa el precio y espera la confirmación antes de salir.' },
    { title: 'Viaja', text: 'Pagas por desbloquear y luego por cada minuto iniciado. Nada más.' },
    { title: 'Devuelve', text: 'Vuelve a un punto marcado y confirma que el viaje terminó.' },
  ],
  city: {
    eyebrow: 'La ciudad',
    title: 'Para lo que está cerca.',
    text: 'Santa Cruz es sombra, árboles en flor y calles anchas. Ronda es para los viajes cortos: el café, la plaza, la oficina, la vuelta a casa.',
    imageAlt: 'Póster de Santa Cruz: torres del centro, techos rojos y nubes en un cielo azul.',
    vehicles: [
      { name: 'Scooter', text: 'Ligero y directo para las cuadras de todos los días.' },
      { name: 'Bici eléctrica', text: 'Un asiento, un poco de ayuda y toda la tarde por delante.' },
    ],
  },
  postersSection: {
    eyebrow: 'Santa Cruz',
    title: 'Lo que notas cuando vas más despacio.',
    featureTitle: 'Moverse también es mirar.',
    featureText: 'La luz de la tarde, el ladrillo y el cielo cruceño están en cada pantalla de Ronda.',
  },
  posters: [
    {
      title: 'Tu ciudad. Otra mirada.',
      text: 'Lo extraordinario también está cerca.',
      alt: 'Ilustración de Santa Cruz al atardecer, con el río y las luces de la ciudad.',
    },
    {
      title: 'Dale aire a tu día.',
      text: 'Encuentra un scooter o una bici eléctrica a pocos pasos.',
      alt: 'Ilustración del Cristo Redentor de Santa Cruz bajo un cielo azul.',
    },
    {
      title: 'Ve a escribir tu historia.',
      text: 'Desbloquea en segundos y recorre la ciudad a tu ritmo.',
      alt: 'Ilustración de la torre de la catedral de Santa Cruz entre palmeras.',
    },
  ],
  pricing: {
    eyebrow: 'Precios',
    title: 'Claro desde el primer minuto.',
    text: 'Ves el precio antes de empezar y el recibo al terminar.',
    unlock: 'Para desbloquear',
    perMinute: 'Por minuto iniciado',
    example: (minutes) => `Por ejemplo, un viaje de ${minutes} minutos cuesta`,
    imageAlt:
      'iPhone con el mapa de Ronda en Equipetrol, con scooters, una bici eléctrica y un botón para escanear el código de un vehículo.',
  },
  faqSection: {
    eyebrow: 'Ayuda',
    title: 'Antes de tu primer viaje.',
    text: 'Las preguntas que más nos hacen, respondidas igual que en la app.',
  },
  faq: [
    {
      question: '¿Dónde puedo devolver el vehículo?',
      answer:
        'En un punto de devolución marcado en el mapa, dentro del área de operación. Solicita el fin del viaje y espera la confirmación.',
    },
    {
      question: '¿Cómo se calcula el precio?',
      answer: `Bs ${formatBs(pricing.unlock, 'es')} por desbloquear y Bs ${formatBs(
        pricing.perMinute,
        'es'
      )} por cada minuto iniciado. Ves el precio antes de empezar y el recibo al terminar.`,
    },
    {
      question: '¿Cerrar la app termina el viaje?',
      answer:
        'No. Hasta que aparezca “Viaje terminado”, la devolución no está confirmada. Cambiar de pestaña, cerrar la app o cerrar sesión no termina el viaje.',
    },
    {
      question: '¿Por qué me piden verificar mi identidad?',
      answer:
        'Antes de tu primer viaje, la app verifica tu identidad con tu documento y una selfie. Se hace una sola vez y queda en tu cuenta.',
    },
    {
      question: 'No puedo iniciar el viaje. ¿Qué hago?',
      answer:
        'Elige un vehículo disponible y revisa su precio. Si el desbloqueo sigue confirmándose, espera el resultado antes de usarlo.',
    },
  ],
  closing: {
    title: 'Tu próximo viaje empieza cerca.',
    text: 'Crea tu cuenta, verifica tu identidad una vez y elige un vehículo en el mapa.',
    badge: 'Muy pronto en iPhone',
    link: 'Mira cómo funciona',
  },
  footer: {
    tagline: 'Scooters y bicis eléctricas para moverte cerca.',
    place: 'Santa Cruz de la Sierra, Bolivia',
    note: 'Vista previa: los vehículos y pagos son simulados.',
  },
};

// Attach the language-independent parts (links, images, colors) to each dictionary.
const withAssets = (t) => ({
  ...t,
  navigation: t.navigation.map((name, i) => ({ name, href: hrefs[i] })),
  steps: t.steps.map((step, i) => ({ ...step, image: stepImages[i] })),
  posters: t.posters.map((poster, i) => ({ ...poster, image: posterImages[i] })),
  city: {
    ...t.city,
    vehicles: t.city.vehicles.map((vehicle, i) => ({ ...vehicle, color: vehicleColors[i] })),
  },
});

export const content = { en: withAssets(en), es: withAssets(es) };
